import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Stack,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";

import {
  createPcpConfig,
  updatePcpConfig,
  getPcps,
  getLocations,
} from "../../../utils/services/control/masterDataService";
import PcpConfigFormFields, {
  buildPcpConfigInitialState,
  buildPcpConfigPayload,
  validatePcpConfig,
} from "../../administration/components/employees/PcpConfigFormFields";

/**
 * PcpConfigDialog — Manage a PCP's configuration.
 *
 * Two tabs:
 *  - "Edit current location": load existing config for `(employeePcpId, currentLocationId)`
 *    and update via PUT /api/MasterData/pcp-config.
 *  - "Add to new location": pick a location where this PCP isn't configured yet
 *    and create a new config via POST /api/MasterData/pcp-config.
 */
export default function PcpConfigDialog({
  open,
  onClose,
  pcp,
  currentLocationId,
  onSaved,
}) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [allLocations, setAllLocations] = useState([]);
  const [pcpConfigs, setPcpConfigs] = useState([]); // configs activos del PCP en distintas locations
  const [supportOptions, setSupportOptions] = useState([]); // PCPs activos para backup

  const [editForm, setEditForm] = useState(buildPcpConfigInitialState());
  const [addForm, setAddForm] = useState(buildPcpConfigInitialState());
  const [addLocationId, setAddLocationId] = useState("");

  const employeePcpId = pcp?.employeePcpId;
  const pcpName = pcp ? `${pcp.title || ""} ${pcp.pcpName || ""}`.trim() : "";

  // Cargar datos cuando se abre
  useEffect(() => {
    if (!open || !employeePcpId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setInfo(null);
      setTab(0);
      setAddLocationId("");
      setAddForm(buildPcpConfigInitialState());

      try {
        const [locsRaw, configs, supportPcps] = await Promise.all([
          getLocations({ clinical: true, status: 1 }),
          getPcps({ employeePcpId }),
          getPcps({ status: 1 }),
        ]);
        if (cancelled) return;

        setAllLocations(Array.isArray(locsRaw) ? locsRaw : []);
        setPcpConfigs(Array.isArray(configs) ? configs : []);
        setSupportOptions(
          Array.isArray(supportPcps)
            ? supportPcps.filter((p) => p.employeePcpId !== employeePcpId)
            : []
        );

        const current = (configs || []).find(
          (c) => c.locationId === currentLocationId
        );
        setEditForm(buildPcpConfigInitialState(current));
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Failed to load PCP data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, employeePcpId, currentLocationId]);

  // Locations donde el PCP NO está aún configurado (para el tab "Add")
  const availableLocations = useMemo(() => {
    const taken = new Set(pcpConfigs.map((c) => c.locationId));
    return allLocations.filter((l) => !taken.has(l.locationId));
  }, [allLocations, pcpConfigs]);

  const currentLocationName = useMemo(() => {
    const found = pcpConfigs.find((c) => c.locationId === currentLocationId);
    return found?.medicalCenter || pcp?.centerName || "—";
  }, [pcpConfigs, currentLocationId, pcp]);

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddField = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setError(null);
    setInfo(null);

    if (tab === 0) {
      const validationError = validatePcpConfig(editForm);
      if (validationError) return setError(validationError);

      setSaving(true);
      try {
        const res = await updatePcpConfig({
          employeePcpId,
          locationId: currentLocationId,
          ...buildPcpConfigPayload(editForm),
        });
        if (res.result !== 0) {
          setError(res.message || "Failed to update PCP config.");
        } else {
          setInfo("PCP configuration updated successfully.");
          onSaved?.();
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Update failed");
      } finally {
        setSaving(false);
      }
    } else {
      if (!addLocationId) return setError("Select a location.");
      const validationError = validatePcpConfig(addForm);
      if (validationError) return setError(validationError);

      setSaving(true);
      try {
        const res = await createPcpConfig({
          employeePcpId,
          locationId: Number(addLocationId),
          ...buildPcpConfigPayload(addForm),
        });
        if (res.result !== 0) {
          setError(res.message || "Failed to create PCP config.");
        } else {
          setInfo(
            `PCP registered in ${
              allLocations.find((l) => l.locationId === Number(addLocationId))
                ?.medicalCenter || "the new location"
            }.`
          );
          // Refrescar configs locales para que el location ya no aparezca disponible
          const refreshed = await getPcps({ employeePcpId });
          setPcpConfigs(Array.isArray(refreshed) ? refreshed : []);
          setAddForm(buildPcpConfigInitialState());
          setAddLocationId("");
          onSaved?.();
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Create failed");
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "rgba(99, 102, 241, 0.12)",
              color: "#4F46E5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TuneIcon fontSize="small" />
          </Box>
          <Stack spacing={-0.3}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1rem", lineHeight: 1.3 }}>
              PCP Configuration
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pcpName || "—"}
              {pcpConfigs.length > 0 &&
                ` • configured in ${pcpConfigs.length} location${
                  pcpConfigs.length !== 1 ? "s" : ""
                }`}
            </Typography>
          </Stack>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: "1px solid #E5E7EB", px: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setError(null);
            setInfo(null);
          }}
        >
          <Tab
            icon={<TuneIcon fontSize="small" />}
            iconPosition="start"
            label="Edit current location"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }}
          />
          <Tab
            icon={<AddLocationAltOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Add to new location"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" py={6} spacing={1.5}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading PCP configuration...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" onClose={() => setInfo(null)} sx={{ borderRadius: 2 }}>
                {info}
              </Alert>
            )}

            {tab === 0 ? (
              <>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Editing configuration for{" "}
                  <strong>{currentLocationName}</strong>. Leave a field empty
                  to clear it. The patient capacity, age range and accepted
                  patient types you set here apply only to this location.
                </Alert>
                <PcpConfigFormFields
                  value={editForm}
                  onChange={handleEditField}
                  supportPcpOptions={supportOptions}
                />
              </>
            ) : (
              <>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Register this PCP in a different clinical location with its
                  own capacity and acceptance rules.
                </Alert>

                <FormControl size="small" fullWidth>
                  <InputLabel>Location *</InputLabel>
                  <Select
                    value={addLocationId}
                    label="Location *"
                    onChange={(e) => setAddLocationId(e.target.value)}
                    disabled={availableLocations.length === 0}
                  >
                    {availableLocations.length === 0 ? (
                      <MenuItem value="" disabled>
                        This PCP is already configured in every clinical location.
                      </MenuItem>
                    ) : (
                      availableLocations.map((l) => (
                        <MenuItem key={l.locationId} value={l.locationId}>
                          {l.medicalCenter}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {pcpConfigs.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Already configured in
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.5 }}>
                      {pcpConfigs.map((c) => (
                        <Chip
                          key={c.locationId}
                          label={c.medicalCenter}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            bgcolor: c.locationId === currentLocationId ? "#EDE9FE" : "#F3F4F6",
                            color: c.locationId === currentLocationId ? "#4F46E5" : "#374151",
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <PcpConfigFormFields
                  value={addForm}
                  onChange={handleAddField}
                  supportPcpOptions={supportOptions}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || (tab === 1 && !addLocationId)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            minWidth: 140,
            background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
          }}
        >
          {saving ? "Saving..." : tab === 0 ? "Save changes" : "Register PCP"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
